import { useEffect, useRef, useState, useCallback } from 'react';
import type { PandaPlayerState } from '@/types/video-synced-article';

interface UseIframePandaPlayerProps {
  embedUrl: string;
  onTimeUpdate?: (currentTime: number) => void;
  onReady?: () => void;
}

/**
 * Hook alternativo que usa iframe do Panda Videos
 * Não requer Library ID, apenas a URL de embed
 */
export function useIframePandaPlayer({
  embedUrl,
  onTimeUpdate,
  onReady,
}: UseIframePandaPlayerProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [state, setState] = useState<PandaPlayerState>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    isReady: false,
    isFullscreen: false,
  });

  /**
   * Escuta mensagens do iframe (PostMessage API)
   */
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verifica se a mensagem vem do Panda Videos
      if (!event.origin.includes('pandavideo.com.br')) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        console.log('📺 Mensagem do Panda:', data); // DEBUG

        // Panda usa "message" como campo principal, não "event"
        const eventType = data.message || data.event;

        switch (eventType) {
          case 'panda_ready': {
            console.log('✅ Player pronto!');
            setState((prev) => ({ ...prev, isReady: true }));
            onReady?.();
            break;
          }

          case 'panda_timeupdate': {
            const currentTime = data.currentTime || 0;
            console.log('⏱️ Tempo atualizado:', currentTime);
            setState((prev) => ({ ...prev, currentTime }));
            onTimeUpdate?.(currentTime);
            break;
          }

          case 'panda_play': {
            console.log('▶️ Play');
            setState((prev) => ({ ...prev, isPlaying: true }));
            break;
          }

          case 'panda_pause':
          case 'panda_ended': {
            console.log('⏸️ Pause/Ended');
            setState((prev) => ({ ...prev, isPlaying: false }));
            break;
          }

          case 'panda_canplay': {
            const duration = data.duration || 0;
            console.log('📏 Duração:', duration);
            setState((prev) => ({ ...prev, duration }));
            break;
          }

          case 'panda_enterfullscreen': {
            setState((prev) => ({ ...prev, isFullscreen: true }));
            break;
          }

          case 'panda_exitfullscreen': {
            setState((prev) => ({ ...prev, isFullscreen: false }));
            break;
          }
        }
      } catch (error) {
        console.error('Erro ao processar mensagem do player:', error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onTimeUpdate, onReady]);

  /**
   * Polling manual como fallback caso PostMessage não funcione
   * Simula progresso baseado em tempo decorrido
   */
  useEffect(() => {
    // Inicia polling após 2 segundos (dá tempo do player carregar)
    const startPolling = setTimeout(() => {
      console.log('🔄 Iniciando polling manual (fallback)');

      let manualTime = 0;
      pollingIntervalRef.current = setInterval(() => {
        // Incrementa tempo a cada segundo (aproximação)
        manualTime += 1;

        setState((prev) => {
          // Só atualiza se não estiver recebendo eventos reais
          if (prev.currentTime === 0 || Math.abs(prev.currentTime - manualTime) > 2) {
            console.log('🔄 Polling: Tempo manual =', manualTime);
            onTimeUpdate?.(manualTime);
            return { ...prev, currentTime: manualTime, isReady: true };
          }
          return prev;
        });
      }, 1000);
    }, 2000);

    return () => {
      clearTimeout(startPolling);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [onTimeUpdate]);

  /**
   * Envia comando para o iframe do Panda Videos
   * Formato oficial: { type: 'comando', parameter: valor }
   * Ref: https://pandavideo.readme.io/reference/send-events
   */
  const sendCommand = useCallback((command: string, value?: unknown) => {
    if (!iframeRef.current?.contentWindow) {
      console.warn('⚠️ Iframe não disponível para comando:', command);
      return;
    }

    console.log('📤 Enviando comando para Panda:', command, value);

    try {
      const target = iframeRef.current.contentWindow;

      // Formato oficial do Panda: { type: 'comando', parameter: valor }
      if (value !== undefined) {
        target.postMessage({ type: command, parameter: value }, '*');
      } else {
        target.postMessage({ type: command }, '*');
      }
    } catch (error) {
      console.error('Erro ao enviar comando:', error);
    }
  }, []);

  /**
   * Controles do player
   * Comandos baseados na API oficial: https://pandavideo.readme.io/reference/send-events
   */
  const play = useCallback(() => {
    sendCommand('play');
  }, [sendCommand]);

  const pause = useCallback(() => {
    sendCommand('pause');
  }, [sendCommand]);

  const togglePlay = useCallback(() => {
    sendCommand('togglePlay');
  }, [sendCommand]);

  const seekTo = useCallback((time: number) => {
    // Comando oficial: { type: 'currentTime', parameter: segundos }
    console.log('🎯 Seekando para:', time, 'segundos');
    sendCommand('currentTime', time);
  }, [sendCommand]);

  const forward = useCallback((seconds: number = 10) => {
    sendCommand('forward', seconds);
  }, [sendCommand]);

  const rewind = useCallback((seconds: number = 10) => {
    sendCommand('rewind', seconds);
  }, [sendCommand]);

  const setVolume = useCallback((volume: number) => {
    // Comando oficial: { type: 'volume', parameter: 0-1 }
    sendCommand('volume', Math.max(0, Math.min(1, volume)));
  }, [sendCommand]);

  return {
    state,
    controls: {
      play,
      pause,
      togglePlay,
      seekTo,
      forward,
      rewind,
      setVolume,
    },
    iframeRef,
    embedUrl,
  };
}
